import type { Season } from './maintenance'

/**
 * Single source of truth for interpreting a maintenance task's cadence.
 *
 * `classifyCadence` turns the resolved (frequency, frequencyDays) pair into a
 * language-agnostic descriptor. A periodic interval is shown in whole months
 * when it divides evenly by 30, else whole weeks when divisible by 7, else as
 * raw days. This module is pure
 * logic — each app renders the descriptor in its own way:
 *   - backend status logic → keys ('seasonal' + inner) decide the RAG path
 *   - ERP (Finnish)        → a local keyed renderer
 *   - resident app (Lingui) → a local keyed renderer
 */

/** Seasons are ~90 days; a seasonal task with a shorter interval is treated as a
 *  within-season cadence (mow weekly in summer) rather than once-a-season. */
export const SEASON_LENGTH_DAYS = 90

export type Cadence =
  | { key: 'months'; n: number } // n>=1; n===1 renders as "monthly"
  | { key: 'weeks'; n: number } // n>=1; n===1 renders as "weekly"
  | { key: 'days'; n: number } // not a whole number of weeks/months
  | { key: 'yearly' } // named annual frequency
  | { key: 'seasonal'; season: Season; inner: Cadence | null }
  | { key: 'asNeeded' } // no recurrence
  | { key: 'raw'; text: string } // unmapped frequency, shown verbatim

const SEASON_KEYS: readonly string[] = ['spring', 'summer', 'fall', 'winter']

/**
 * Scheduling-accurate cadence model — the SINGLE source of cadence facts.
 * Unlike the display `Cadence` (which flattens to whole months via ÷30 for
 * labels), this preserves the calendar-month / fixed-day / seasonal distinction
 * that date math depends on. `classifyCadence` (display) derives from this.
 */
export type CadenceModel =
  | { kind: 'periodic'; days: number } // fixed-day interval, year-round
  | { kind: 'interval'; months: number } // calendar-month interval
  | { kind: 'annual' } // calendar-year interval
  | { kind: 'seasonal'; season: Season; innerDays: number | null } // every innerDays in-season, or once-per-season
  | { kind: 'oneTime' } // no recurrence
  | { kind: 'unknown'; raw: string } // unmapped non-null frequency — surfaced, not swallowed

export function resolveCadenceModel(
  frequency: string | null | undefined,
  frequencyDays?: number | null,
): CadenceModel {
  const days = frequencyDays ?? 0
  const freq = frequency?.toLowerCase() ?? null

  if (!freq) return days > 0 ? { kind: 'periodic', days } : { kind: 'oneTime' }

  if (SEASON_KEYS.includes(freq)) {
    // A seasonal interval shorter than a season is a within-season cadence
    // (mow weekly in summer); otherwise it's a once-per-season task.
    const innerDays = days > 0 && days < SEASON_LENGTH_DAYS ? days : null
    return { kind: 'seasonal', season: freq as Season, innerDays }
  }

  if (freq === 'annually') return { kind: 'annual' }
  if (freq === 'monthly') return { kind: 'interval', months: 1 }
  if (freq === 'quarterly') return { kind: 'interval', months: 3 }
  if (freq === 'biannually') return { kind: 'interval', months: 6 }

  // Do NOT silently bucket an unknown value as asNeeded (#8). Surface it so
  // scheduling can fail loud and display can show the raw string.
  return { kind: 'unknown', raw: frequency! }
}

/** A periodic day interval: whole months (÷30), else whole weeks (÷7), else days. */
function periodicCadence(days: number): Cadence {
  if (days % 30 === 0) return { key: 'months', n: days / 30 }
  if (days % 7 === 0) return { key: 'weeks', n: days / 7 }
  return { key: 'days', n: days }
}

/**
 * Display descriptor for a cadence, derived from the canonical
 * `resolveCadenceModel`. The model is the single source of cadence facts; this
 * only maps it to presentation buckets (and may flatten — e.g. both calendar
 * quarterly and a 90-day periodic interval render as "3 months").
 */
export function classifyCadence(
  frequency: string | null | undefined,
  frequencyDays?: number | null,
): Cadence {
  const model = resolveCadenceModel(frequency, frequencyDays)
  switch (model.kind) {
    case 'oneTime':
      return { key: 'asNeeded' }
    case 'unknown':
      return { key: 'raw', text: model.raw }
    case 'annual':
      return { key: 'yearly' }
    case 'interval':
      return { key: 'months', n: model.months }
    case 'periodic':
      return periodicCadence(model.days)
    case 'seasonal':
      return {
        key: 'seasonal',
        season: model.season,
        inner: model.innerDays ? periodicCadence(model.innerDays) : null,
      }
  }
}
